import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Form, Select } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import { TEmploee, useLazyGetEmployeesQuery } from 'app/services/edpApiV1/employees';

type TUserInputProps = {
  label?: string | Element | ReactNode;
  rules?: { required: boolean; message?: string }[];
  className?: string;
  disabled?: boolean;
  formItemName?: string;
  organizationId: string;
};

const SIZE = 10;
let timeout: any;

const UserInput: FC<TUserInputProps> = ({
  rules,
  label = 'Сотрудник',
  formItemName = 'employeeId',
  className,
  disabled,
  organizationId,
}) => {
  const [getEmployees, employees] = useLazyGetEmployeesQuery();
  const [usersArray, setUsersArray] = useState<TEmploee[]>([]);

  const page = useRef<number>(1);
  const fromOnChange = useRef<boolean>(false);

  const defaultParams = {
    search: '',
    page: 1,
    size: SIZE,
    isActive: true,
    organizationId,
    allAvailableOrganizations: false,
  };

  useEffect(() => {
    if (employees.data) {
      if (fromOnChange.current) {
        setUsersArray(employees!.data!.data);
      } else {
        setUsersArray([...usersArray, ...employees!.data!.data]);
      }
      fromOnChange.current = false;
    }
  }, [employees.data]);

  const onSearch = (val: string) => {
    clearTimeout(timeout);
    page.current = 1;
    timeout = setTimeout(() => {
      fromOnChange.current = true;
      getEmployees({
        page: page.current,
        size: SIZE,
        search: val,
        isActive: true,
        organizationId,
        allAvailableOrganizations: false,
      });
    }, 500);
  };

  const onFocus = () => {
    page.current = 1;
    fromOnChange.current = true;
    getEmployees({
      search: '',
      page: page.current,
      size: SIZE,
      isActive: true,
      organizationId,
      allAvailableOrganizations: false,
    });
  };

  useEffect(() => {
    onFocus();
  }, [organizationId]);

  // бесконечный скролл, запрашиваем пользователей до момента, пока не придут все
  const handleScroll = (scrollEvent: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { target } = scrollEvent;
    if (target instanceof HTMLDivElement) {
      if (target.scrollHeight - target.scrollTop === target.clientHeight) {
        if ((employees.data?.pagination?.total ?? 0) > usersArray?.length) {
          getEmployees({
            page: ++page.current,
            size: SIZE,
            search: '',
            isActive: true,
            organizationId,
            allAvailableOrganizations: false,
          });
        }
      }
    }
  };

  const onSelectEmployee = (id: string, option: DefaultOptionType & { data: TEmploee }) => {
    onFocus();
  };

  const onClearEmployee = () => {
    page.current = 1;
    getEmployees(defaultParams);
  };

  useEffect(() => {
    getEmployees(defaultParams);
  }, []);

  return (
    <Form.Item name={formItemName} label={label} rules={rules} className={className}>
      <Select
        disabled={disabled}
        showSearch
        placeholder="Поиск сотрудника по ФИО"
        onSelect={onSelectEmployee}
        allowClear
        loading={employees?.isLoading || employees?.isFetching}
        onClear={onClearEmployee}
        filterOption={false}
        onSearch={onSearch}
        onPopupScroll={handleScroll}
        options={usersArray.map(item => ({
          label: `${item.lastName} ${item.name} ${item.middleName ?? ''} / ${item.personnelNumber}`,
          value: item.id,
          data: item,
        }))}
      />
    </Form.Item>
  );
};

export default UserInput;
